import React, { useState, useEffect, useRef } from 'react';
import { Video, Upload, FolderOpen, X, Check, Loader2, Link2, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import mediaApi from '@/api/media';
import { apiV1 } from '@/api/client';
import { parseVideoUrl, youtubeThumbnail } from '@/lib/videoEmbed';

const VIDEO_TYPES = ['video/mp4', 'video/webm'];
const DEFAULT_MAX_VIDEO_SIZE_MB = 50;

/**
 * VideoPicker - A reusable component for selecting/uploading videos
 * 
 * Features:
 * - Upload new videos directly (MP4/WebM)
 * - Select from Media Library
 * - Paste a video URL (YouTube, Vimeo or a direct MP4/WebM link)
 * - Preview current video
 * 
 * @param {string} value - Current video URL
 * @param {function} onChange - Callback when video changes (receives URL string)
 * @param {string} label - Optional label for the field
 * @param {string} placeholder - Placeholder text for URL input
 * @param {string} className - Additional CSS classes
 * @param {boolean} showPreview - Show video preview (default: true)
 * @param {string} folder - Media library folder for uploads
 * @param {string} testId - data-testid attribute
 */
export function VideoPicker({
  value,
  onChange,
  label,
  placeholder = "Enter video URL or select from library",
  className,
  showPreview = true,
  folder = null,
  testId = "video-picker",
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('library');
  const [mediaItems, setMediaItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [urlInput, setUrlInput] = useState('');
  const [maxVideoSizeMb, setMaxVideoSizeMb] = useState(DEFAULT_MAX_VIDEO_SIZE_MB);
  const fileInputRef = useRef(null);

  // Use the server's video limit so the picker never accepts a file the
  // server will refuse.
  useEffect(() => {
    if (!isOpen) return;
    apiV1.get('/media/config')
      .then((res) => {
        const mb = res.data?.data?.max_video_size_mb;
        if (mb) setMaxVideoSizeMb(mb);
      })
      .catch(() => {});
  }, [isOpen]);

  // Load media library when dialog opens
  useEffect(() => {
    if (isOpen) {
      loadMediaLibrary();
      setUrlInput(value || '');
      setSelectedVideo(null);
    }
  }, [isOpen]);

  const loadMediaLibrary = async () => {
    setLoading(true);
    try {
      const result = await mediaApi.list({ media_type: 'video', limit: 100 });
      if (result.success) {
        setMediaItems(result.data || []);
      }
    } catch (error) {
      console.error('Failed to load media library:', error);
      toast.error('Failed to load media library');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!VIDEO_TYPES.includes(file.type)) {
      toast.error('Please select an MP4 or WebM video');
      return;
    }

    if (file.size > maxVideoSizeMb * 1024 * 1024) {
      toast.error(`Video must be ${maxVideoSizeMb}MB or smaller. Compress it, or upload it to YouTube or Vimeo and paste the link.`);
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    
    try {
      // Same auth + folder handling as ImagePicker; videos aren't optimised.
      // A folder like "pages/abc123" is a path, not a folder ID.
      const uploadOptions = { optimize_for_web: false };
      if (folder) {
        if (folder.includes('/') || !folder.match(/^[a-f0-9-]{36}$/i)) {
          uploadOptions.folder_path = folder;
        } else {
          uploadOptions.folder = folder;
        }
      }
      const result = await mediaApi.upload(file, uploadOptions);
      
      if (result.success) {
        toast.success('Video uploaded successfully!');
        // Refresh library and select the new video
        await loadMediaLibrary();
        setSelectedVideo(result.data.url);
        setActiveTab('library');
      } else {
        toast.error(typeof result.detail === 'string' ? result.detail : 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload video');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleConfirm = () => {
    if (activeTab === 'url' && urlInput) {
      if (parseVideoUrl(urlInput)?.kind === 'unknown') {
        toast.error("That link can't play here. Use a YouTube or Vimeo link, or a direct MP4/WebM file.");
        return;
      }
      onChange(urlInput.trim());
    } else if (selectedVideo) {
      onChange(selectedVideo);
    }
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange('');
    setSelectedVideo(null);
    setUrlInput('');
  };

  const getVideoUrl = (item) => {
    if (typeof item === 'string') return item;
    return item.url || `/uploads/${item.file_path}`;
  };

  const previewVideo = parseVideoUrl(value);

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className={cn("space-y-2", className)} data-testid={testId}>
      {label && <Label>{label}</Label>}
      
      {/* Current Value Display / Trigger */}
      <div className="flex gap-2">
        <div className="flex-1 relative">
          <Input
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="pr-20"
            data-testid={`${testId}-url-input`}
          />
          <div className="absolute right-1 top-1/2 -translate-y-1/2 flex gap-1">
            {value && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={handleClear}
                data-testid={`${testId}-clear-btn`}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => setIsOpen(true)}
              data-testid={`${testId}-browse-btn`}
            >
              <FolderOpen className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Preview */}
      {showPreview && value && (
        <div 
          className="relative rounded-lg border overflow-hidden bg-black aspect-video"
          data-testid={`${testId}-preview`}
        >
          {previewVideo?.kind === 'youtube' ? (
            <img src={youtubeThumbnail(previewVideo)} alt="" className="w-full h-full object-cover" />
          ) : previewVideo?.kind === 'vimeo' ? (
            <div className="w-full h-full flex items-center justify-center text-white/70 text-sm">Vimeo video</div>
          ) : previewVideo?.kind === 'unknown' ? (
            <div className="w-full h-full flex items-center justify-center text-white/70 text-sm px-4 text-center">
              This link can't play as a video
            </div>
          ) : (
            <video
              src={value}
              className="w-full h-full object-cover"
              muted
              loop
              playsInline
              autoPlay
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          )}
          <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
            <Play className="w-3 h-3" />
            {previewVideo?.kind === 'youtube' ? 'YouTube' : previewVideo?.kind === 'vimeo' ? 'Vimeo' : 'Video'}
          </div>
        </div>
      )}

      {/* Media Library Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Video className="h-5 w-5" />
              Select Video
            </DialogTitle>
            <DialogDescription>
              Upload a new video, select from your media library, or paste a URL
            </DialogDescription>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="library" className="flex items-center gap-2">
                <FolderOpen className="h-4 w-4" />
                Library
              </TabsTrigger>
              <TabsTrigger value="upload" className="flex items-center gap-2">
                <Upload className="h-4 w-4" />
                Upload
              </TabsTrigger>
              <TabsTrigger value="url" className="flex items-center gap-2">
                <Link2 className="h-4 w-4" />
                URL
              </TabsTrigger>
            </TabsList>

            {/* Library Tab */}
            <TabsContent value="library" className="mt-4">
              <ScrollArea className="h-[400px] pr-4">
                {loading ? (
                  <div className="grid grid-cols-3 gap-3">
                    {[...Array(6)].map((_, i) => (
                      <Skeleton key={i} className="aspect-video rounded-lg" />
                    ))}
                  </div>
                ) : mediaItems.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-[300px] text-muted-foreground">
                    <Video className="h-12 w-12 mb-4 opacity-50" />
                    <p className="text-sm">No videos in your library</p>
                    <Button
                      variant="link"
                      className="mt-2"
                      onClick={() => setActiveTab('upload')}
                    >
                      Upload your first video
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-3">
                    {mediaItems.map((item) => {
                      const videoUrl = getVideoUrl(item);
                      const isSelected = selectedVideo === videoUrl;
                      return (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => setSelectedVideo(videoUrl)}
                          className={cn(
                            "relative aspect-video rounded-lg overflow-hidden border-2 transition-all bg-black group",
                            isSelected 
                              ? "border-primary ring-2 ring-primary/20" 
                              : "border-transparent hover:border-muted-foreground/30"
                          )}
                          data-testid={`media-item-${item.id}`}
                        >
                          <video
                            src={videoUrl}
                            className="w-full h-full object-cover"
                            muted
                            preload="metadata"
                            onMouseEnter={(e) => e.target.play()}
                            onMouseLeave={(e) => { e.target.pause(); e.target.currentTime = 0; }}
                          />
                          <div className="absolute bottom-1 left-1 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded">
                            {formatFileSize(item.file_size)}
                          </div>
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <Play className="w-8 h-8 text-white drop-shadow-lg" />
                          </div>
                          {isSelected && (
                            <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                              <div className="bg-primary text-primary-foreground rounded-full p-1">
                                <Check className="h-4 w-4" />
                              </div>
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>

            {/* Upload Tab */}
            <TabsContent value="upload" className="mt-4">
              <div className="flex flex-col items-center justify-center h-[400px] border-2 border-dashed rounded-lg">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="video/mp4,video/webm"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid={`${testId}-file-input`}
                />
                
                {uploading ? (
                  <div className="flex flex-col items-center gap-4">
                    <Loader2 className="h-12 w-12 animate-spin text-primary" />
                    <p className="text-sm text-muted-foreground">Uploading video...</p>
                    <p className="text-xs text-muted-foreground">This may take a moment for larger files</p>
                  </div>
                ) : (
                  <>
                    <Video className="h-12 w-12 text-muted-foreground mb-4" />
                    <p className="text-sm text-muted-foreground mb-4">
                      Drag and drop a video, or click to browse
                    </p>
                    <Button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      data-testid={`${testId}-upload-btn`}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Choose Video
                    </Button>
                    <p className="text-xs text-muted-foreground mt-4">
                      MP4 or WebM up to {maxVideoSizeMb}MB
                    </p>
                    <p className="text-xs text-muted-foreground">
                      For best results, use MP4 with H.264 codec
                    </p>
                  </>
                )}
              </div>
            </TabsContent>

            {/* URL Tab */}
            <TabsContent value="url" className="mt-4">
              <div className="space-y-4">
                <div>
                  <Label>Video URL</Label>
                  <Input
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    placeholder="https://youtu.be/... or https://vimeo.com/... or a .mp4 link"
                    className="mt-1"
                    data-testid={`${testId}-url-field`}
                  />
                </div>
                
                {urlInput && (
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">Preview</Label>
                    <div className="relative aspect-video rounded-lg border overflow-hidden bg-black">
                      <video
                        src={urlInput}
                        className="w-full h-full object-contain"
                        controls
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleConfirm}
              disabled={(activeTab === 'library' && !selectedVideo) && (activeTab !== 'url' || !urlInput)}
              data-testid={`${testId}-confirm-btn`}
            >
              <Check className="h-4 w-4 mr-2" />
              Select Video
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default VideoPicker;
